/**
 * Pipelines feature UI state — selected stage, sort, pagination, filter draft.
 * Mirrors the calendar feature's `useEditViewState` split: this store owns
 * client-only state; server-shaped data lives in `data/usePipelinesSource.ts`.
 *
 * Stays in zustand forever; never moves to SWR.
 */
import { create } from 'zustand'
import type { ManagerId, StatusId } from '@ludus/core'
import type { FilterDraft, SortState } from '../types.ts'
import { EMPTY_FILTER_DRAFT } from '../types.ts'

const DEFAULT_SORT: SortState = { key: 'age', direction: 'desc' }

/** Default rows-per-page. 7 read too cramped once the all-leads view landed. */
export const DEFAULT_PAGE_SIZE = 20

interface StageState {
  selectedStageId: StatusId | null
  sort: SortState
  page: number
  pageSize: number
  filters: FilterDraft

  selectStage(id: StatusId): void
  setSort(sort: SortState): void
  toggleSort(key: SortState['key']): void
  setPage(page: number): void
  setPageSize(size: number): void
  setFilters(patch: Partial<FilterDraft>): void
  toggleManagerFilter(id: ManagerId): void
  resetFilters(): void
}

export const useStageState = create<StageState>((set, get) => ({
  selectedStageId: null,
  sort: DEFAULT_SORT,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: EMPTY_FILTER_DRAFT,

  selectStage(id) {
    // Toggle: re-selecting the focused stage deselects it (→ pipeline-wide
    // "all leads" view). No auto-select on mount, so null is the resting state.
    set({ selectedStageId: get().selectedStageId === id ? null : id, page: 0 })
  },

  setSort(sort) {
    set({ sort, page: 0 })
  },

  toggleSort(key) {
    const current = get().sort
    if (current.key !== key) {
      set({ sort: { key, direction: 'desc' }, page: 0 })
      return
    }
    set({
      sort: { key, direction: current.direction === 'asc' ? 'desc' : 'asc' },
      page: 0,
    })
  },

  setPage(page) {
    set({ page: Math.max(0, page) })
  },

  setPageSize(size) {
    set({ pageSize: Math.max(1, size), page: 0 })
  },

  setFilters(patch) {
    set((state) => ({ filters: { ...state.filters, ...patch }, page: 0 }))
  },

  toggleManagerFilter(id) {
    set((state) => {
      const has = state.filters.managerIds.includes(id)
      return {
        filters: {
          ...state.filters,
          managerIds: has
            ? state.filters.managerIds.filter((m) => m !== id)
            : [...state.filters.managerIds, id],
        },
        page: 0,
      }
    })
  },

  resetFilters() {
    set({ filters: EMPTY_FILTER_DRAFT, page: 0 })
  },
}))
